import React, { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, CircularProgress } from '@mui/material';
import { ZuoraPaymentMethod } from '@klab-berlin/api-sdk/lib/services/billing';
import { useTranslation } from 'react-i18next';
import config from 'config';

import { borderRadius, palette, spaces } from '../../assets/styles/themes/tokens';
import { selectUser } from '../../reducers/user.selectors';
import { updatePaymentMethod } from '../../actions/user.actions';
import useAction from '../../utils/useAction';
import { getPaymentMethodData } from '../../utils/payment';
import { ZuoraPaymentPageResponse } from '../../types/ZuoraPayments';
import { UserPaymentMethodType } from '../../types/User';
import PaymentContent from './PaymentContent';
import { addError } from '../../actions/errors.actions';
import { NotificationContext } from '../../state/NotificationContextProvider';

interface PaymentMethodsFormProps {
  selectedMethodType: string;
  onPaymentMethodUpdated: () => void;
}

const PaymentMethodsForm = ({
  selectedMethodType,
  onPaymentMethodUpdated,
}: PaymentMethodsFormProps) => {
  const { t } = useTranslation();

  const updatePaymentMethodAction = useAction(updatePaymentMethod);
  const { showNotification } = useContext(NotificationContext);

  const user = useSelector(selectUser);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onClientSidePaymentErrorHandler = () => {
    setIsLoading(false);
  };

  const onResponse = () => {
    setIsLoading(false);
    showNotification({
      message: t('All changes were saved successfully'),
      severity: 'success',
    });
    onPaymentMethodUpdated();
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);

    window.Z.submit();
  };

  const onResponseHandler = (paymentMethod: ZuoraPaymentMethod, response: ZuoraPaymentPageResponse) => {
    if (!user) return setIsLoading(false);

    if (!response.success) {
      addError('Error while setting up payment method, please refresh');
      return setIsLoading(false);
    }

    const paymentData = {
      type: paymentMethod,
      paymentMethodId: response.refId,
      braintreeToken: response.auth,
      licenceId: user.licenceId
    };

    if (!paymentData) return setIsLoading(false);

    const paymentMethodId = paymentData?.paymentMethodId || paymentData?.braintreeToken;

    if (!paymentMethodId) return setIsLoading(false);

    const newPaymentMethod = getPaymentMethodData(paymentData?.type, paymentMethodId);

    updatePaymentMethodAction(newPaymentMethod).finally(onResponse);
  };

  if (!user) {
    return <></>;
  }

  const idButtonHidden = selectedMethodType !== UserPaymentMethodType.SEPA &&
    selectedMethodType !== UserPaymentMethodType.CREDIT_CARD;

  return (
    <Box
      component='form'
      onSubmit={onSubmit}
    >
      <PaymentContent
        selectedMethod={selectedMethodType}
        onResponseHandler={onResponseHandler}
        onClientSideErrorHandler={onClientSidePaymentErrorHandler}
        zuoraHostedPagesSource={config.zuoraSinglePageUpgradePaymentSource}
      />
      <Button
        data-testid='payment-form-submit'
        type='submit'
        variant='contained'
        size='large'
        hidden={idButtonHidden}
        disabled={isLoading}
        sx={{
          backgroundColor: palette.primary.main,
          color: palette.common.white,
          borderRadius: borderRadius.sm,
          padding: `${spaces.xs} ${spaces.lg}`
        }}
      >
        {isLoading ?
          <CircularProgress size={26} sx={{
            color: palette.common.white,
            '& .MuiCircularProgress-svg': {
              height: 'auto',
              width: 'auto',
            },
          }} /> : t('Continue to confirmation')}
      </Button>
    </Box>
  );
};

export default PaymentMethodsForm;
